import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import {
  Users,
  CalendarCheck,
  CheckCircle,
  Clock,
  XCircle,
  Hotel,
  TrendingUp,
  ArrowUpRight,
  MapPin,
} from "lucide-react";
import { apiService } from "../services/api";
import { Hotel as HotelType } from "../types/hotel";
import { STATUS_MAP } from "../utils/helper";
import { storeDistricts, storeOwners, storeRegions } from "../utils/auth";

interface DashboardStats {
  totalHotels: number;
  last30Days: number;
  totalGuests: number;
  guestsLast30Days: number;
  totalBookings: number;
  totalRevenue: number;
  approvedHotels: number;
  pendingHotels: number;
  rejectedHotels: number;
  totalOwners: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalHotels: 0,
    last30Days: 0,
    totalGuests: 0,
    guestsLast30Days: 0,
    totalBookings: 0,
    totalRevenue: 0,
    approvedHotels: 0,
    pendingHotels: 0,
    rejectedHotels: 0,
    totalOwners: 0
  });
  const [hotelsList, setHotelsList] = useState<HotelType[]>([]);
  const [dashboardData, setDashboardData] = useState<any>([]);

  useEffect(() => {
    fetchStats();
    //fetchHotels();
  }, []);

  const fetchStats = async () => {
    try {
      const dashboardData = await apiService.getDashboardData();
      setDashboardData (dashboardData);
      setHotelsList(dashboardData.data?.properties);
      storeRegions (dashboardData.data?.regions);
      storeDistricts (dashboardData.data?.districts);
      storeOwners (dashboardData.data?.owners);

      setStats({
        totalHotels: dashboardData?.data.propStats?.totalHotels,
        last30Days: dashboardData?.data.propStats?.last30Days,
        totalGuests: dashboardData?.data.customerStats?.totalCustomers,
        guestsLast30Days: dashboardData?.data.customerStats?.last30DaysCustomers,
        totalBookings: 0,
        totalRevenue: 125000,
        approvedHotels: dashboardData?.data.propStats?.approved,
        pendingHotels: dashboardData?.data.propStats?.pending,
        rejectedHotels: dashboardData?.data.propStats?.rejected,
        totalOwners: dashboardData?.data.ownerStats?.totalOwners
      });
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setStats({
        totalHotels: 0,
        last30Days: 0,
        totalGuests: 0,
        guestsLast30Days: 0,
        totalBookings: 0,
        totalRevenue: 0,
        approvedHotels: 0,
        pendingHotels: 0,
        rejectedHotels: 0,
        totalOwners: 0,
      });
    }
  };

 

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-50 text-green-700 border-green-200";
      case "pending":
        return "bg-warm-100 text-warm-700 border-warm-300";
      case "rejected":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-beige-100 text-sage-700 border-beige-300";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle size={12} />;
      case "pending":
        return <Clock size={12} />;
      case "rejected":
        return <XCircle size={12} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-serif font-bold text-sage-900">Dashboard</h1>
          <p className="text-sm text-sage-500 mt-1">Overview of your hotel management platform</p>
        </div>

        {/* Primary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {[
            {
              title: "Total Hotels",
              value: stats.totalHotels,
              icon: Hotel,
              iconBg: "bg-brand-50",
              iconColor: "text-brand-500",
              change: stats.last30Days + " (last 30 days)",
            },
            {
              title: "Total Customers",
              value: stats.totalGuests.toLocaleString(),
              icon: Users,
              iconBg: "bg-green-50",
              iconColor: "text-green-600",
              //change: "+12% growth",
              change: stats.guestsLast30Days + " (last 30 days)",
            },
            {
              title: "Total Owners",
              value: stats.totalOwners,
              icon: CalendarCheck,
              iconBg: "bg-warm-100",
              iconColor: "text-warm-600",
              change: "Active Owners",
            },
          ].map((stat) => (
            <div
              key={stat.title}
              className="bg-white rounded-xl border border-beige-300 p-5 hover:shadow-card-hover transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2.5 rounded-lg ${stat.iconBg}`}>
                  <stat.icon size={18} className={stat.iconColor} />
                </div>
              </div>
              <p className="text-2xl font-bold text-sage-900">{stat.value}</p>
              <p className="text-sm text-sage-500 mt-0.5">{stat.title}</p>
              <p className="text-xs text-sage-400 mt-2">{stat.change}</p>
            </div>
          ))}
        </div>

        {/* Verification Status Row */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">
            Verification Status
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: "Approved",
                value: stats.approvedHotels,
                icon: CheckCircle,
                color: "text-green-600",
                bg: "bg-green-50",
                border: "border-green-200",
                barColor: "bg-green-500",
              },
              {
                title: "Pending Review",
                value: stats.pendingHotels,
                icon: Clock,
                color: "text-warm-600",
                bg: "bg-warm-100",
                border: "border-warm-300",
                barColor: "bg-warm-500",
              },
              {
                title: "Rejected",
                value: stats.rejectedHotels,
                icon: XCircle,
                color: "text-red-600",
                bg: "bg-red-50",
                border: "border-red-200",
                barColor: "bg-red-400",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-xl border border-beige-300 p-5 relative overflow-hidden"
              >
                <div className={`absolute top-0 left-0 w-full h-0.5 ${item.barColor}`} />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-sage-500 font-medium">{item.title}</p>
                    <p className="text-3xl font-bold text-sage-900 mt-1">{item.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-lg ${item.bg}`}>
                    <item.icon size={18} className={item.color} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Hotels Table */}
        <div className="bg-white rounded-xl border border-beige-300 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-beige-200">
            <div>
              <h2 className="text-base font-semibold text-sage-900">Recent Hotels</h2>
              <p className="text-xs text-sage-500 mt-0.5">{hotelsList.length} total listings</p>
            </div>
            <button
              onClick={() => navigate("/hotels")}
              className="flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors"
            >
              View all
              <ArrowUpRight size={14} />
            </button>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-beige-50 border-b border-beige-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-sage-500 uppercase tracking-wider">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {hotelsList.slice(0, 6).map((h) => {
                  const status = (h as any).verificationStatus || "unknown";
                  const propertyName = (h as any).propertyName || h.propName || "";
                  const basePrice = (h as any).basePrice;

                  return (
                    <tr
                      key={h.id}
                      onClick={() => navigate(`/hotels/${h.id}`)}
                      className="hover:bg-beige-50/60 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {(h as any).image ? (
                            <img
                              src={(h as any).image}
                              alt={propertyName}
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                              <Hotel size={16} className="text-brand-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-sage-900 truncate">{propertyName}</p>
                            <p className="text-xs text-sage-500">{h.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-sage-600 min-w-0">
                          <MapPin size={13} className="text-sage-400 flex-shrink-0" />
                          <span className="truncate max-w-[200px]">
                            {/* {(h as any).region || h.city}, {(h as any).district || ""} */}
                            {h.address}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-sage-900">{(h as any).ownerName || "-"}</p>
                        <p className="text-xs text-sage-500">{(h as any).ownerPhone || ""}</p>
                      </td>
                      {/* <td className="px-5 py-4">
                        <span className="inline-block text-xs font-medium text-sage-600 bg-beige-100 px-2.5 py-1 rounded-md capitalize">
                          {(h as any).accommodationType || "-"}
                        </span>
                      </td> */}
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${getStatusColor(
                            status
                          )}`}
                        >
                          {getStatusIcon(status)}
                          <span className="capitalize">{STATUS_MAP[h.status || 1]}</span>
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <p className="text-sm font-semibold text-sage-900">
                          {basePrice ? `$${basePrice}` : "-"}
                        </p>
                        <p className="text-xs text-sage-500">per night</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-beige-200">
            {hotelsList.slice(0, 6).map((h) => {
              const status = (h as any).verificationStatus || "unknown";
              const propertyName = (h as any).propertyName || h.propName || "";
              const basePrice = (h as any).basePrice;

              return (
                <div
                  key={h.id}
                  onClick={() => navigate(`/hotels/${h.id}`)}
                  className="p-4 hover:bg-beige-50/60 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3 min-w-0">
                      {(h as any).image ? (
                        <img
                          src={(h as any).image}
                          alt={propertyName}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                          <Hotel size={16} className="text-brand-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-sage-900 truncate">{propertyName}</p>
                        <p className="text-xs text-sage-500">{(h as any).ownerName || "-"}</p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${getStatusColor(
                        status
                      )}`}
                    >
                      {getStatusIcon(status)}
                      <span className="capitalize">{status}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-sage-500 pl-[52px]">
                    <span>{(h as any).region || h.city}</span>
                    <span className="font-semibold text-sage-900">
                      {basePrice ? `Tzs ${basePrice}/night` : "-"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
